import { hex, type Color } from "../../common/color.js";
import type { Anchor } from "../core/Control.js";
import type { Alignment, Insets, Size } from "../../common/geometry.js";
import { insets } from "../../common/geometry.js";
import type { FontSpec } from "../render/GlyphAtlas.js";
import type { ThemeMetrics, ThemePalette } from "../themes/Theme.js";

/**
 * A size written in markup.
 *
 * The whole point of the dialect is that most of these are never written at all — an absent
 * `width` is `auto`, and a layout made of auto, stars and a couple of `em`s survives every
 * resize, every `uiScale` and every theme. The kinds that remain are the ones a person has a
 * real reason to type.
 */
export type Length =
  | { kind: "auto" }
  /** Logical pixels. */
  | { kind: "px"; value: number }
  /** Multiples of the theme's font size, so the layout grows with the type. */
  | { kind: "em"; value: number }
  /** A share of the leftovers, by weight: `*` is 1, `2*` is 2. */
  | { kind: "star"; value: number }
  /** A share of the whole axis, 0 to 1. */
  | { kind: "percent"; value: number };

export const AUTO: Length = { kind: "auto" };

/**
 * Read a length, or `null` if it is unreadable.
 *
 * Null rather than a throw, and `auto` rather than null at the call sites that have a sensible
 * default, because a layout with one mistyped width should still come up — with a warning
 * naming the attribute — rather than leaving the user with a blank canvas. `parseGridLength`
 * already takes the same view of a bad track.
 */
export function parseLength(text: string): Length | null {
  const s = text.trim().toLowerCase();
  if (s === "" || s === "auto") return AUTO;

  if (s.endsWith("*")) {
    const head = s.slice(0, -1).trim();
    if (head === "") return { kind: "star", value: 1 };
    const weight = Number.parseFloat(head);
    return Number.isFinite(weight) && weight > 0 ? { kind: "star", value: weight } : null;
  }

  if (s.endsWith("%")) {
    const share = Number.parseFloat(s.slice(0, -1));
    return Number.isFinite(share) && share >= 0 ? { kind: "percent", value: share / 100 } : null;
  }

  if (s.endsWith("em")) {
    const ems = Number.parseFloat(s.slice(0, -2));
    return Number.isFinite(ems) ? { kind: "em", value: ems } : null;
  }

  const px = Number.parseFloat(s.endsWith("px") ? s.slice(0, -2) : s);
  return Number.isFinite(px) ? { kind: "px", value: px } : null;
}

/** True for the kinds that turn into a plain number without knowing the room available. */
export function isAbsolute(length: Length): boolean {
  return length.kind === "px" || length.kind === "em";
}

/**
 * Turn an absolute length into pixels.
 *
 * `em` is the reason zoom needs no thought anywhere else in the dialect: it is resolved against
 * the theme's font size, every coordinate in the framework is a logical pixel, and `uiScale`
 * multiplies logical pixels at paint time. A layout written in `em` therefore follows the type
 * size, the theme and the user's scale at once, without a single one of the three being named.
 */
export function toPixels(length: Length, metrics: ThemeMetrics): number {
  switch (length.kind) {
    case "px":
      return length.value;
    case "em":
      return length.value * metrics.font.size;
    default:
      return 0;
  }
}

/**
 * Read `margin="8"`, `"8 16"`, `"8 16 12"` or `"8 16 12 4"`.
 *
 * WPF's order — left, then top, then right, then bottom — because that is the order
 * `insets(l, t, r, b)` takes and the order every other Webwin call site is written in. CSS reads
 * the four the other way round, which is worth knowing but not worth being inconsistent for.
 */
export function parseInsets(text: string, metrics: ThemeMetrics): Insets | null {
  const parts = text.trim().split(/[\s,]+/).filter(Boolean);
  if (parts.length === 0 || parts.length > 4) return null;

  const values: number[] = [];
  for (const part of parts) {
    const length = parseLength(part);
    if (!length || !isAbsolute(length)) return null;
    values.push(toPixels(length, metrics));
  }

  const [a, b, c, d] = values;
  if (values.length === 1) return insets(a!);
  if (values.length === 2) return insets(a!, b!);
  if (values.length === 3) return insets(a!, b!, c!);
  return insets(a!, b!, c!, d!);
}

/** Read `640x480` or `640 480`. */
export function parseSize(text: string, metrics: ThemeMetrics): Size | null {
  const parts = text.trim().split(/[\sx×,]+/i).filter(Boolean);
  if (parts.length !== 2) return null;
  const w = parseLength(parts[0]!);
  const h = parseLength(parts[1]!);
  if (!w || !h || !isAbsolute(w) || !isAbsolute(h)) return null;
  return { width: toPixels(w, metrics), height: toPixels(h, metrics) };
}

export function parseBoolean(text: string): boolean | null {
  const s = text.trim().toLowerCase();
  if (s === "true") return true;
  if (s === "false") return false;
  return null;
}

/** A plain number, which may also be written in `em` so spacing scales with the type. */
export function parseNumber(text: string, metrics: ThemeMetrics): number | null {
  const length = parseLength(text);
  if (!length || !isAbsolute(length)) return null;
  return toPixels(length, metrics);
}

/** Read `left top right`, or `all`, or `none`. */
export function parseAnchor(text: string): Partial<Anchor> | null {
  const parts = text.trim().toLowerCase().split(/[\s,]+/).filter(Boolean);
  if (parts.length === 0) return null;
  if (parts.length === 1 && parts[0] === "none") {
    return { left: false, top: false, right: false, bottom: false };
  }
  if (parts.length === 1 && parts[0] === "all") {
    return { left: true, top: true, right: true, bottom: true };
  }
  // An anchor is merged over a left/top default, so naming any edge means naming all of them:
  // `anchor="right"` has to clear left, or it would silently mean "stretch".
  const anchor: Anchor = { left: false, top: false, right: false, bottom: false };
  for (const part of parts) {
    if (part !== "left" && part !== "top" && part !== "right" && part !== "bottom") return null;
    anchor[part] = true;
  }
  return anchor;
}

const WEIGHT_WORDS: Record<string, number> = { normal: 400, regular: 400, bold: 700 };

/**
 * Read `"bold 15 Consolas"`, in any order. `italic` may appear anywhere too.
 *
 * A bare number is ambiguous between a size and a weight, and the rule that resolves it is the
 * one that matches how people write: **a lone number is the size**, because `font="15"` is the
 * common case and nobody means a 600-pixel caption. A weight written as a number — `600` — is
 * only read as one when a size is there beside it, which is the only time it is unambiguous.
 * `bold` and `normal` are never ambiguous and work on their own.
 */
export function parseFont(text: string, metrics: ThemeMetrics): FontSpec | null {
  const parts = text.trim().split(/[\s,]+/).filter(Boolean);
  if (parts.length === 0) return null;

  let weight: number | undefined;
  let italic = false;
  const numbers: number[] = [];
  const family: string[] = [];

  for (const part of parts) {
    if (part.toLowerCase() === "italic") {
      italic = true;
      continue;
    }
    const named = WEIGHT_WORDS[part.toLowerCase()];
    if (named !== undefined) {
      weight = named;
      continue;
    }
    const length = parseLength(part);
    if (length && isAbsolute(length) && /^[\d.]/.test(part)) {
      numbers.push(toPixels(length, metrics));
      continue;
    }
    family.push(part);
  }

  const isWeight = (n: number): boolean => n >= 100 && n <= 900 && n % 100 === 0;
  let size: number | null = null;
  if (numbers.length === 1) {
    size = numbers[0]!;
  } else if (numbers.length >= 2) {
    const weightAt = numbers.findIndex(isWeight);
    if (weightAt >= 0 && weight === undefined) {
      weight = numbers[weightAt];
      size = numbers.find((_, i) => i !== weightAt) ?? null;
    } else {
      size = numbers[numbers.length - 1]!;
    }
  }

  if (size === null && family.length === 0 && weight === undefined && !italic) return null;
  return {
    family: family.length > 0 ? family.join(" ") : metrics.font.family,
    size: size ?? metrics.font.size,
    ...(weight === undefined ? {} : { weight }),
    ...(italic ? { italic } : {}),
  };
}

/**
 * Read `#rrggbb`, or the name of a color in the theme's palette.
 *
 * Naming a palette slot is the useful half: a layout that says `accent` is a layout that follows
 * every theme, and one that says `#3478f6` is a layout that has opted out of them.
 */
export function parseColor(text: string, palette: ThemePalette): Color | null {
  const s = text.trim();
  if (s === "") return null;
  if (s.startsWith("#")) {
    try {
      return hex(s);
    } catch {
      return null;
    }
  }
  const named = (palette as unknown as Record<string, Color | undefined>)[s];
  return named ?? null;
}

/** The palette slots a color attribute may name, for an error that has to suggest something. */
export function paletteNames(palette: ThemePalette): string[] {
  return Object.keys(palette);
}

/** Read `"Work, Home, Ideas"`. Commas only: a list item may contain spaces. */
export function parseList(text: string): string[] {
  return text
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/** Read a whitespace-or-comma separated track list, turning `em` into pixels as it goes. */
export function parseTracks(text: string, metrics: ThemeMetrics): string[] {
  return text
    .trim()
    .split(/[\s,]+/)
    .filter(Boolean)
    .map((part) => {
      const length = parseLength(part);
      if (length && length.kind === "em") return String(toPixels(length, metrics));
      return part;
    });
}

export const ALIGNMENTS: readonly Alignment[] = ["stretch", "start", "center", "end"];

/**
 * Translate `_File` into the `&File` the menu code reads.
 *
 * Written with an underscore because an ampersand in XML has to be `&amp;`, and a caption that
 * reads `&amp;File` in the source is a caption nobody can scan. `__` is a literal underscore,
 * and a literal `&` is doubled on the way out so `parseMnemonic` leaves it alone.
 */
export function toMnemonic(text: string): string {
  let out = "";
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]!;
    if (ch === "&") {
      out += "&&";
      continue;
    }
    if (ch !== "_") {
      out += ch;
      continue;
    }
    if (text[i + 1] === "_") {
      out += "_";
      i++;
      continue;
    }
    out += "&";
  }
  return out;
}

/**
 * Turn an `onXxx` attribute name into the event name the emitter uses.
 *
 * Webwin's events are one flat lowercase list — `selectionchanged`, `textchanged` — and the
 * attribute is the camelCase spelling of the same thing, which is what the control's own
 * `onSelectionChanged` option is called. Strip the `on`, lowercase the rest.
 */
export function eventNameOf(attribute: string): string {
  return attribute.slice(2).toLowerCase();
}

export function isEventAttribute(name: string): boolean {
  return name.length > 2 && name.startsWith("on") && name[2] === name[2]!.toUpperCase();
}
